import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Plus, Clock, CheckCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const TicketList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    ticket_type: 'general_support',
    subject: '',
    body: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  const handleDelete = async (e, ticketId) => {
    e.stopPropagation(); // Prevent card click
    
    if (!window.confirm('Are you sure you want to permanently delete this ticket?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket deleted successfully');
      fetchTickets(); // Refresh list
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete ticket');
    }
  };

  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/tickets`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Support ticket created successfully');
      setDialogOpen(false);
      setFormData({ ticket_type: 'general_support', subject: '', body: '' });
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-purple-100 text-purple-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700'
    };
    return styles[status] || styles.open;
  };

  const getTypeBadge = (type) => {
    const styles = {
      general_support: 'bg-blue-100 text-blue-700',
      removal_request: 'bg-red-100 text-red-700',
      technical_issue: 'bg-orange-100 text-orange-700',
      content_report: 'bg-purple-100 text-purple-700'
    };
    return styles[type] || styles.general_support;
  };

  const formatType = (type) => {
    const labels = {
      general_support: 'General Support',
      removal_request: 'Removal Request',
      technical_issue: 'Technical Issue',
      content_report: 'Content Report'
    };
    return labels[type] || type;
  };

  const filteredTickets = Array.isArray(tickets) ? tickets.filter(ticket => {
    if (filter === 'open') return ['open', 'pending', 'in_progress'].includes(ticket.status);
    if (filter === 'resolved') return ['resolved', 'closed'].includes(ticket.status);
    return true;
  }) : [];

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-pulse">
          <MessageSquare className="w-16 h-16 text-green-500 mx-auto mb-4" />
        </div>
        <p className="text-gray-600">Loading support tickets...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800" data-testid="support-title">Support Tickets</h2>
          <p className="text-gray-600 mt-1">Contact admin or request assistance</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
              data-testid="create-ticket-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
              <DialogDescription>Describe your issue or request</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ticket_type">Ticket Type</Label>
                <Select
                  value={formData.ticket_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, ticket_type: value }))}
                >
                  <SelectTrigger data-testid="ticket-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general_support">General Support</SelectItem>
                    <SelectItem value="technical_issue">Technical Issue</SelectItem>
                    <SelectItem value="content_report">Content Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Brief description of your issue"
                  required
                  data-testid="ticket-subject-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="body">Details</Label>
                <Textarea
                  id="body"
                  value={formData.body}
                  onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                  placeholder="Please provide as much detail as possible..."
                  rows={6}
                  required
                  data-testid="ticket-body-input"
                />
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700" data-testid="submit-ticket-btn">
                  Create Ticket
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-green-600' : ''}
          data-testid="filter-all-tickets"
        >
          All ({tickets.length})
        </Button>
        <Button
          variant={filter === 'open' ? 'default' : 'outline'}
          onClick={() => setFilter('open')}
          className={filter === 'open' ? 'bg-green-600' : ''}
          data-testid="filter-open-tickets"
        >
          Open ({tickets.filter(t => ['open', 'pending', 'in_progress'].includes(t.status)).length})
        </Button>
        <Button
          variant={filter === 'resolved' ? 'default' : 'outline'}
          onClick={() => setFilter('resolved')}
          className={filter === 'resolved' ? 'bg-green-600' : ''}
          data-testid="filter-resolved-tickets"
        >
          Resolved ({tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length})
        </Button>
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4" data-testid="no-tickets-message">
              {filter === 'all' ? 'No support tickets yet' : `No ${filter} tickets`}
            </p>
            {filter === 'all' && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Ticket
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate(`/vendor/dashboard/support/${ticket.id}`)}
              data-testid={`ticket-card-${ticket.id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg" data-testid={`ticket-subject-${ticket.id}`}>
                      {ticket.subject}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-2">
                  {['resolved', 'closed'].includes(ticket.status) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                      onClick={(e) => handleDelete(e, ticket.id)}
                      data-testid={`delete-ticket-btn-${ticket.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}

                      <Badge className={getTypeBadge(ticket.ticket_type)}>
                        {formatType(ticket.ticket_type)}
                      </Badge>
                      <Badge className={getStatusBadge(ticket.status)} data-testid={`ticket-status-${ticket.id}`}>
                        {ticket.status === 'open' && <Clock className="w-3 h-3 mr-1" />}
                        {['resolved', 'closed'].includes(ticket.status) && <CheckCircle className="w-3 h-3 mr-1" />}
                        {ticket.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 line-clamp-2" data-testid={`ticket-body-${ticket.id}`}>
                  {ticket.body}
                </p>
                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                  <span>Created: {new Date(ticket.created_at).toLocaleDateString()}</span>
                  {ticket.updated_at !== ticket.created_at && (
                    <span>Updated: {new Date(ticket.updated_at).toLocaleDateString()}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TicketList;